import { useTranslation } from "react-i18next";
import { useTheme } from "../../utils/contexts/ThemeContext";
import { useEffect, useState } from "react";
import { Modal, Form } from 'react-bootstrap';
import './ModalCustomizeTheme.scss'

interface ThemeColorItem {
    name: string;
    label: string;
    value: string;
}
interface ThemeImageItem {
    name: string;
    label: string;
    value: string;
}

interface ModalCustomizeThemeProps {
    showModal: boolean;
    onHide: () => void;
}

const ModalCustomizeTheme: React.FC<ModalCustomizeThemeProps> = ({ showModal, onHide }) => {
    const { t } = useTranslation()
    const { theme, UpdateCustomThemeColor, UpdateCustomThemeImage,resetCustomTheme, getCustomThemeImage, toggleTheme } = useTheme()
    const defaultColors: ThemeColorItem[] = [
        { name: 'background-primary', label: 'Background Primary', value: '#d7e8ea' },
        { name: 'background-secondary', label: 'Background Secondary', value: '#ffffff' },
        { name: 'accent-color', label: 'Accent Color', value: '#325e56' },
        { name: 'text-primary', label: 'Text Primary', value: '#000000' },
        { name: 'text-secondary', label: 'Text Secondary', value: '#bdc3c7' },
        { name: 'sidebar', label: 'Sidebar', value: '#ceaa86' },
        { name: 'topbar', label: 'Topbar', value: '#325e56' },
        { name: 'sidebar-text', label: 'Sidebar' + ' Text', value: '#ffffff' },
        { name: 'topbar-text', label: 'Topbar' + ' Text', value: '#ffffff' },

    ];
    const defaultImages: ThemeImageItem[] = [
        { name: 'logo', label: 'Logo', value: '' },
        { name: 'icon', label: 'Icon', value: '' },
        { name: 'machine', label: 'Machine', value: '' },
    ];
    const [themeColors, setThemeColors] = useState<ThemeColorItem[]>(defaultColors);
    const [themeImages, setThemeImages] = useState<ThemeImageItem[]>(defaultImages);

    useEffect(() => {
        // Carregar colors guardats al obrir el modal
        if (showModal) {
            try {
                const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
                const updatedColors = themeColors.map(color => {
                    const savedColor = customTheme[color.name];
                    return savedColor ? { ...color, value: savedColor } : color;
                });
                setThemeColors(updatedColors);

                const updatedImages = themeImages.map(image => {
                    const savedImage = getCustomThemeImage(image.name);
                    return savedImage ? { ...image, value: savedImage } : image;
                });
                setThemeImages(updatedImages);

            } catch (error) {
                console.error('Error loading custom theme:', error);
            }
        }
    }, [showModal]);

    const handleColorChange = (index: number, value: string) => {
        setThemeColors(prev => {
            const updated = prev.map((color, idx) =>
                idx === index ? { ...color, value } : color
            );

            // Aplica si estem en tema "custom" els canvis al css per a la vista prèvia
            if (theme === 'custom') {
                const { name } = updated[index];
                document.documentElement.style.setProperty(`--${name}`, value);
            }

            return updated;
        });
    };

    const handleImageChange = (index: number, file: File | null) => {
        if (!file) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
            const imgSrc = reader.result as string;

            setThemeImages(prev => {
                const updated = prev.map((image, idx) =>
                    idx === index ? { ...image, value: imgSrc } : image
                );
                return updated;
            });
        };
    };

    //Guardar tots els nom i valor de themecolors
    const handleSave = () => {
        themeColors.forEach(color => {
            UpdateCustomThemeColor(color.name, color.value);
        });
        themeImages.forEach(image => {
            if(image.value){
                UpdateCustomThemeImage(image.name, image.value)
            }
        })
        toggleTheme("custom");
        onHide();
    };

    const handleReset = () => {
        resetCustomTheme();
        setThemeColors(defaultColors);
        setThemeImages(defaultImages);
    };

    const handleModalClose = () => {
        // Restaurar el tema actual si se cierra sin guardar
        if (theme === 'custom') {
            try {
                const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
                Object.entries(customTheme).forEach(([name, value]) => {
                    if (typeof value === 'string' && !name.startsWith('image-')) {
                        document.documentElement.style.setProperty(`--${name}`, value as string);
                    }
                });
            } catch (error) {
                console.error('Error restoring custom theme:', error);
            }
        }
        onHide();
    };

    return (
        <Modal
            show={showModal}
            onHide={handleModalClose}
            keyboard={false}
            backdrop={'static'}
            className="modal-customize-theme"
            centered>
            <Modal.Header className="position-relative d-block modalThemeHeader">
                <p className="modal-title-custom m-0 text-start text-lg-center">{t('Customize Theme')}</p>
                <button type='button' className='btn-close custom-close position-absolute end-0 top-50 translate-middle-y me-3' aria-label='Close' onClick={handleModalClose} style={{ color: 'var(--text-primary)' }}> </button>
            </Modal.Header>
            <Modal.Body className="mb-0">
                <div className="row">
                    {[0, 1].map((colIndex) => (
                        <div key={colIndex} className="col">
                            {themeColors
                                .filter((_, i) => i % 2 === colIndex)
                                .map((color, index) => {
                                    const originalIndex = colIndex + index * 2;
                                    return (
                                        <div key={originalIndex} className="theme-color-item mb-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <p className="theme-color-label mb-0">{t(color.label)}</p>
                                                <div
                                                    className="color-preview ms-2"
                                                    style={{ backgroundColor: color.value }}
                                                />
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <Form.Control
                                                    type="color"
                                                    value={color.value}
                                                    className="color-picker me-2"
                                                    onChange={(e) => handleColorChange(originalIndex, e.target.value)}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={color.value}
                                                    className="color-input"
                                                    onChange={(e) => handleColorChange(originalIndex, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ))}
                </div>
                <hr />
                <div className="row">
                    {[0, 1].map((colIndex) => (
                        <div key={colIndex} className="col">
                            {themeImages
                                .filter((_, i) => i % 2 === colIndex)
                                .map((image, index) => {
                                    const originalIndex = colIndex + index * 2;
                                    return (
                                        <div key={originalIndex} className="theme-color-item mb-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <p className="theme-color-label mb-0">{t(image.label)}</p>
                                                {image.value && (
                                                    <button 
                                                        className="btn btn-sm ms-2 p-0"
                                                        style={{ color: 'var(--text-secondary)' }}
                                                        onClick={() => {
                                                            setThemeImages(prev => prev.map((img, idx) => 
                                                                idx === originalIndex ? { ...img, value: '' } : img
                                                            ));
                                                        }}> <i className="bi bi-x" style={{ color: 'var(--text-primary)' }}></i> </button>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center">
                                            <div className="image-preview me-2" >
                                                    {image.value ? (
                                                        <img 
                                                            src={image.value} 
                                                            alt={image.label} 
                                                            className="img-thumbnail"
                                                            style={{ height: '38px', width: '38px', objectFit: 'contain', margin: 0 }}
                                                        />
                                                    ) : (
                                                        <div className="icon-holder rounded-1 text-center" style={{ height: '38px', width: '38px', margin: 0, padding: "5px", border: '1px solid #e7e7e7', color: "black" }}>
                                                            <i className="bi bi-image " ></i>
                                                        </div>
                                                    )}
                                                </div>
                                                <Form.Control 
                                                    name={image.name} 
                                                    accept="image/*" 
                                                    type="file" 
                                                    className="inputStyle ms-2" 
                                                    style={{color: 'var(--text-primary)', flex: 1}}
                                                    onChange={(e) => {
                                                        const file = (e.target as HTMLInputElement).files?.[0] || null;
                                                        handleImageChange(originalIndex, file);
                                                    }} 
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ))}
                    </div>
            </Modal.Body>
            <Modal.Footer className="modalThemeFooter">
                <div className="d-flex gap-2 text-end">
                    {/* <button className="btn-Custom btn-IDRed" onClick={handleModalClose}>
                        <i className="bi bi-x-circle"></i>
                    </button> */}
                    <button className="btn-Custom btn-IDYellow" onClick={handleReset}>
                        <i className="bi bi-arrow-counterclockwise"></i>
                    </button>
                    <button className="btn-Custom btn-IDGreen" onClick={handleSave}>
                        <i className="bi bi-save"></i>
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
export default ModalCustomizeTheme;